import type { AccountDetails } from "@iluvatar/global/src/utilities/account-setup";
import type { User, UserCredential } from "firebase/auth";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { hasPermission } from "@iluvatar/global/src/utilities/permissions";

import { clearDocumentSubscriptions } from "@/hooks/document-subscriptions";
import { getAccountSession } from "@/services/account-session";
import {
  getFreshAuthToken,
  isSessionAuthError,
  waitForAuthReady,
} from "@/services/auth-session";
import {
  getAuthRuntime,
  observeAuthToken,
} from "@/services/firebase-auth.runtime";
import { isTrustedComputer } from "@/services/trusted-computer";
import { clearSessionUsers } from "@/services/user-session-cache";

import { useDataStore } from "../dataStore.context";
import { clearQueryCaches } from "../react-query/query-client";
import { TDecodedToken } from "./authentication.types";

type AuthenticationContextType = {
  isAuthenticated: boolean;
  hasPermission: (permissionFlag: number) => boolean;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  ssoLogin: (
    provide: "google.com" | "apple.com" | string,
    email?: string,
  ) => Promise<void | [User, TDecodedToken]>;
  ssoRedirectHandler: (
    result?: UserCredential | null,
  ) => Promise<void | [User, TDecodedToken]>;
  login: (
    email: string,
    password: string,
  ) => Promise<void | [User, TDecodedToken]>;
  initialized: boolean;
  user: User | null;
  uid?: string;
  authToken: TDecodedToken | null;
  accountDetails: AccountDetails | null;
  acceptedTC?: string;
};

export const AuthenticationContext = createContext<AuthenticationContextType>({
  isAuthenticated: false,
  hasPermission: () => false,
  logout: async () => {},
  login: async () => {
    return;
  },
  ssoLogin: async () => {
    return;
  },
  ssoRedirectHandler: async () => {
    return;
  },
  reload: async () => {},
  initialized: false,
  user: null,
  uid: undefined,
  authToken: null,
  accountDetails: null,
});

export const availableProviders: string[] = ["google.com", "apple.com"];

export async function getSSOProvider(providerId: string) {
  const { GoogleAuthProvider, OAuthProvider } = await getAuthRuntime();
  if (!availableProviders.includes(providerId)) {
    throw new Error("Provider Id is not supported");
  }
  const provider =
    providerId === "google.com"
      ? new GoogleAuthProvider()
      : new OAuthProvider("apple.com");
  provider.addScope(providerId === "google.com" ? "profile" : "name");
  provider.addScope("email");
  if (providerId === "google.com") {
    provider.setCustomParameters({ prompt: "select_account" });
  }
  return provider;
}

export function AuthenticationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { cleanStoreData } = useDataStore();
  const [session, setSession] = useState<{
    user: User | null;
    token: TDecodedToken | null;
    accountDetails: AccountDetails | null;
  }>({ user: null, token: null, accountDetails: null });
  const [initialized, setInitialized] = useState(false);
  const previousUid = useRef<string | null>();

  const setSessionData = useCallback(
    (
      user: User | null,
      token: TDecodedToken | null,
      accountDetails: AccountDetails | null = null,
    ) => {
      if (previousUid.current !== (user?.uid ?? null)) {
        clearDocumentSubscriptions();
        clearSessionUsers(
          previousUid.current === undefined ? user?.uid : undefined,
        );
        clearQueryCaches(!user || Boolean(previousUid.current));
        cleanStoreData();
        previousUid.current = user?.uid ?? null;
      }
      setSession({ user, token, accountDetails });
    },
    [cleanStoreData],
  );

  const authenticate = useCallback(
    async (
      user: User,
      forceRefresh = false,
    ): Promise<[User, TDecodedToken]> => {
      const { auth } = await getAuthRuntime();
      const { token, accountDetails } = await getAccountSession(
        user,
        forceRefresh,
      );
      if (auth.currentUser?.uid !== user.uid) {
        throw new Error("Authentication changed during sign-in");
      }
      setSessionData(user, token, accountDetails);
      return [user, token];
    },
    [setSessionData],
  );

  useEffect(() => {
    // Remove the previous version's persisted private responses/token query.
    try {
      localStorage.removeItem("REACT_QUERY_OFFLINE_CACHE");
    } catch {
      /* Storage can be disabled. */
    }
    let active = true;
    let version = 0;
    const unsubscribe = observeAuthToken(
      async (user, { auth }) => {
        const currentVersion = ++version;
        try {
          const account = user ? await getAccountSession(user) : null;
          if (
            active &&
            currentVersion === version &&
            auth.currentUser?.uid === user?.uid
          ) {
            setSessionData(
              user,
              account?.token ?? null,
              account?.accountDetails ?? null,
            );
          }
        } catch (error) {
          if (
            active &&
            currentVersion === version &&
            auth.currentUser === user
          ) {
            if (isSessionAuthError(error)) {
              await auth.signOut();
              setSessionData(null, null);
            } else if (user) {
              // Keep a signed-in user during a transient network failure.
              if (previousUid.current !== user.uid) {
                setSessionData(user, null);
              }
            }
          }
        } finally {
          if (active && currentVersion === version) {
            setInitialized(true);
          }
        }
      },
      (error) => {
        if (active) {
          setInitialized(true);
          // oxlint-disable-next-line no-console -- The login form/route guard also surfaces startup failures.
          console.error("Unable to initialize authentication", error);
        }
      },
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }, [setSessionData]);

  useEffect(() => {
    let refreshing = false;
    let lastRefresh = Date.now();
    const refresh = async () => {
      const runtime = await getAuthRuntime().catch(() => undefined);
      if (!runtime) {
        return;
      }
      const { auth } = runtime;
      if (
        refreshing ||
        !auth.currentUser ||
        !navigator.onLine ||
        document.visibilityState !== "visible"
      ) {
        return;
      }
      if (Date.now() - lastRefresh < 30 * 60 * 1000) {
        return;
      }
      refreshing = true;
      try {
        await getFreshAuthToken();
        lastRefresh = Date.now();
      } catch {
        // Transient/offline failures leave the session intact; retry on the next wakeup.
      } finally {
        refreshing = false;
      }
    };
    const timer = window.setInterval(refresh, 30 * 60 * 1000);
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  const logout = useCallback(async () => {
    const { auth } = await getAuthRuntime();
    const { disablePushNotifications } =
      await import("@/services/push-notifications");
    await disablePushNotifications();
    await auth.signOut();
    setSessionData(null, null);
    if (!isTrustedComputer()) {
      const { clearFirestoreCache } = await import("@/services/firebase.init");
      await clearFirestoreCache();
    }
  }, [setSessionData]);
  const reload = useCallback(async () => {
    const { auth } = await getAuthRuntime();
    if (auth.currentUser) {
      await authenticate(auth.currentUser, true);
    }
  }, [authenticate]);
  const login = useCallback(
    async (email: string, password: string) => {
      await waitForAuthReady();
      const { auth, signInWithEmailAndPassword } = await getAuthRuntime();
      const result = await signInWithEmailAndPassword(auth, email, password);
      return authenticate(result.user);
    },
    [authenticate],
  );
  const ssoLogin = useCallback(async (providerId: string, email?: string) => {
    const provider = await getSSOProvider(providerId);
    const { auth, signInWithRedirect, browserPopupRedirectResolver } =
      await getAuthRuntime();
    if (email) {
      provider.setCustomParameters({ login_hint: email });
    }
    await waitForAuthReady();
    // Firebase returns to the current URL after the provider completes sign-in.
    const previousUrl = window.location.href;
    const returnUrl = new URL(previousUrl);
    returnUrl.searchParams.set("sso", "pending");
    window.history.replaceState(window.history.state, "", returnUrl);
    try {
      await signInWithRedirect(auth, provider, browserPopupRedirectResolver);
    } catch (error) {
      window.history.replaceState(window.history.state, "", previousUrl);
      throw error;
    }
  }, []);
  const ssoRedirectHandler = useCallback(
    async (result?: UserCredential | null) => {
      if (!result?.user) {
        throw new Error("No sign-in redirect result");
      }
      return authenticate(result.user);
    },
    [authenticate],
  );
  const userHasPermission = useCallback(
    (flag: number) =>
      Boolean(
        session.token?.claims && hasPermission(session.token.claims, flag),
      ),
    [session.token],
  );

  const value = useMemo(
    () => ({
      isAuthenticated: Boolean(session.user),
      initialized,
      reload,
      logout,
      login,
      ssoLogin,
      ssoRedirectHandler,
      hasPermission: userHasPermission,
      acceptedTC: session.token?.claims?.tc,
      user: session.user,
      uid: session.user?.uid,
      authToken: session.token,
      accountDetails: session.accountDetails,
    }),
    [
      session,
      initialized,
      reload,
      logout,
      login,
      ssoLogin,
      ssoRedirectHandler,
      userHasPermission,
    ],
  );
  return (
    <AuthenticationContext.Provider value={value}>
      {children}
    </AuthenticationContext.Provider>
  );
}

export const useAuthenticationContext = () => useContext(AuthenticationContext);
